import React, { useEffect, useRef, useState } from 'react';
import PaginationLink from '../shared/PaginationLink';
import {
  buildPageHref,
  buildSearchHref,
  getPageFromQuery,
  getSearchFromQuery,
  hasMoreAlgoliaPages,
  stripListingParams,
} from '../../util/PaginationUtil';
import { Constants } from '../../util/Constants';
import {
  assemble,
  createFiltersFromRedirect,
  correctInternalLink,
  createFilterDataPartners,
  getAlgoliaDatasetValue,
  getAlgoliaIndex,
  isItUndefined,
  isLinkAbsolute,
  marketoOptions as options,
  pushSearchSuggestionClickEvent,
} from '../../util/DataUtil';
import ResourceFilter from '../resources/resource-filter';
import { useWindowSize } from '../../util/PageUtil';
import Loading from '../layout/Loading';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { SearchBarPlacement } from '../../constants/SearchBarPlacement';
import { FilterName} from "../../constants/FilterName";
import {
  sendClickedObjectIDsAfterSearch,
  sendNoResultsEvent,
} from '../../util/AlgoliaInsights';

const PartnersCardsBlock = ({
  lang,
  global,
  categories,
  data,
  initialPartners,
}) => {
  // The on-page experience is the original one: Show More grows a single list,
  // Show Less collapses it back, and each request returns the whole list so the
  // results are replaced rather than appended.
  //
  // What is new is that the size of that list is now expressed as a page number
  // in the URL. `?page=3` means "three pages' worth", so a reload shows exactly
  // what the visitor was looking at, and a crawler walking the chain reaches
  // every partner.
  //
  // That only works if one click equals one page. The button used to add 9 to a
  // first page of 8, which no page number can describe — so the step is now the
  // page size. First load is unchanged at 8; each click adds 8 instead of 9.
  const router = useRouter();

  const PARTNERS_PER_PAGE = 8;

  const serverResults = initialPartners;
  const serverHits = Array.isArray(serverResults?.hits) ? serverResults.hits : [];

  const [algoliaData, setAlgoliaData] = useState(serverResults || []);
  const [facetFilters, setFacetFilters] = useState([]);
  const algoliaDataset = getAlgoliaDatasetValue('4') + getAlgoliaIndex();

  // Seeded from the URL — see the note in handleSearch.
  const [query, setQuery] = useState(() => {
    const term = getSearchFromQuery(router?.query);
    return term ? [term] : [];
  });
  // Which page of the series is on screen. Seeded from the URL so a reload or
  // a shared link lands on the same one.
  const [page, setPage] = useState(() => getPageFromQuery(router?.query));
  // One page per request, like every other listing. This used to be
  // `(page + 1) * PARTNERS_PER_PAGE` — one query that re-fetched the whole
  // list from the start and grew with every click. SEO asked for `?page=N` to
  // be page N alone (2026-10-12), so the request matches the page and Show
  // More appends the next one.
  const hitsPerPage = PARTNERS_PER_PAGE;
  const [showMore, setShowMore] = useState(serverHits);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [filterProps, setFilterProps] = useState([]);
  const [languageFilter, setLanguageFilter] = useState([]);
  const [applyFilter, setApplyFilter] = useState(false);
  const [filter, setFilter] = useState(0);
  const [anchorToTop, setAnchorToTop] = useState(0);

  let facetFiltersArray = [];
  let categoryName = [];
  let lngArray = [];

  const windowSize = useWindowSize();
  const linkText =
    data.field_internal_link_text?.[0]?.value?.trim() || 'Learn More';
  // The slice for the current page is already rendered from the server on the
  // first pass, so the initial run is skipped — it would otherwise duplicate.
  // The first slice is already rendered from the server; refetching it on
  // mount would only replace it with itself.
  // Server-rendered results are always unfiltered, so when the URL carries a
  // filter or a search term the client has to fetch again — skipping that left
  // the filter showing as selected while the list underneath stayed unfiltered.
  const urlNarrowsResults =
    Object.keys(stripListingParams(router?.query) || {}).length > 0 ||
    Boolean(getSearchFromQuery(router?.query));

  const skipInitialFetch = useRef(serverHits.length > 0 && !urlNarrowsResults);

  // The deepest page fetched so far, so a fetch can tell "Show More was
  // clicked" from "a filter or a search changed and this list needs
  // rebuilding". Seeded from the URL: the server rendered that page already.
  const lastFetchedPage = useRef(getPageFromQuery(router?.query));

  useEffect(() => {
    if (skipInitialFetch.current) {
      skipInitialFetch.current = false;
      return;
    }
    fetchAlgoliaData();
  }, [page]);

  useEffect(() => {
    if (!lang) {
      return;
    }
    fetchAlgoliaData();
  }, [applyFilter, query, filter]);

  useEffect(() => {
    if (anchorToTop > 0) {
      let doc = document.getElementById('partners-cards-container');
      window.scrollTo({ top: doc?.offsetTop - 80, behavior: 'smooth' });
    }
  }, [anchorToTop]);

  const fetchAlgoliaData = () => {
    if (!algoliaData?.hits?.length) {
      setIsLoading(true);
    }
    let url = createFilterDataPartners(
      lngArray,
      facetFiltersArray,
      facetFilters,
      query,
      hitsPerPage,
      page,
      lang,
      algoliaDataset,
      categoryName
    );

    const fetchAlgolia = async () => {
      await fetch(url, options)
        .then((response) => response.json())
        .then((newResponse) => {
          // Appends only when this is a step forward in the series — Show
          // More. A filter or a search rebuilds the list from its first page.
          //
          // The cards render from `algoliaData.hits`, so the accumulated list
          // has to live there. The rest of the response is taken from the new
          // one: `nbPages` and `page` are what the Show More link reads to
          // decide whether another page exists.
          const isLoadMore = page > lastFetchedPage.current;
          lastFetchedPage.current = page;

          setAlgoliaData((previous) =>
            isLoadMore
              ? {
                  ...newResponse,
                  hits: [...(previous?.hits || []), ...newResponse.hits],
                }
              : newResponse
          );
          if (newResponse.hits.length === 0 && query.length > 0) {
            sendNoResultsEvent(newResponse.queryID, algoliaDataset);
          }
        })
        .finally(() => {
          if (!algoliaData?.hits?.length) {
            setIsLoading(false);
          }
        });
    };
    fetchAlgolia();
  };

  const loadMore = (nextPage) => {
    setPage(nextPage);
  };

  const handleShowLess = () => {
    if (page > 0) {
      setPage(0);
      setAnchorToTop(anchorToTop + 1);
      router.replace(buildPageHref(router.asPath, 0), undefined, {
        shallow: true,
        scroll: false,
      });
    }
  };

  const clearQuery = () => {
    setQuery([]);
    // Only when a term is actually in the URL. This is handed to the filter
    // component as a prop and fires while a filter is being applied — rewriting
    // the address unconditionally raced with the filter's own push and wiped
    // the filter straight back out of it.
    if (getSearchFromQuery(router.query)) {
      router.replace(buildSearchHref(router.asPath, ''), undefined, {
        shallow: true,
        scroll: false,
      });
    }
    setIsSearchActive(false);
    setIsFilterActive(true);
  };

  const clearAll = () => {
    setFilterProps([]);
    triggerFilter([]);
    setLanguageFilter([]);
    // setQuery([]);
    if (query.length > 0) {
      setQuery([]);
    }
    setShowMore([]);
    setFilter(!filter);
    setPage(0);
    setIsFilterActive(false);
  };

  // `resetPage` is false when the filters are being hydrated from the URL
  // rather than chosen by the visitor. Someone opening a shared
  // `?type=blueprints&search=te&page=2` has not changed anything — treating
  // that as a filter change reset the page to one and stripped it from the
  // address before they saw it.
  // Partners never read its filters back out of the URL — the shared filter
  // component wrote them, but nothing here applied them on load. That was
  // invisible while the URL was not worth sharing; now that it carries the page
  // and the search term too, a shared or reloaded link has to restore the
  // filter selection as well.
  //
  // `resetPage: false` because arriving at a link is not the visitor changing a
  // filter — the page in the URL has to survive.
  const triggerFilterFromRedirect = (filter) => {
    // Flat list, not `assemble(categories)`. The matcher walks the array it is
    // given and compares `field_source_translation` on each entry — the
    // assembled version nests children under their parents, so a child filter
    // like a metro is no longer in the top level and never matched.
    const filters = createFiltersFromRedirect(filter, categories || []);

    if (filters?.length) {
      triggerFilter(filters, false);
      setIsFilterActive(true);
    }
  };

  const hydratedFromUrl = useRef(false);

  useEffect(() => {
    if (hydratedFromUrl.current || typeof window === 'undefined') {
      return;
    }

    const urlFilters = stripListingParams(router.query);

    if (urlFilters && Object.keys(urlFilters).length > 0) {
      hydratedFromUrl.current = true;
      triggerFilterFromRedirect(urlFilters);
    }
  }, [categories]);

  const triggerFilter = (filters, resetPage = true) => {
    setFilterProps(filters);

    let searchFilters = [];
    for (let filter of filters) {
      // searchFilters.push(filter.field_source_translation);
      searchFilters.push(filter);
    }

    setFacetFilters(searchFilters);
    // Back to the first page: the filtered set is a different, shorter list, so
    // staying on page three would ask for a slice of it that does not exist and
    // the results would come back empty.
    //
    // Skipped when `resetPage` is false — that is the URL being hydrated on
    // arrival, where the page in the address has to survive.
    if (resetPage) {
      setPage(0);
    }

    // The address itself is deliberately not touched here. The filter control
    // writes the URL and drops the page while doing it, and `router.asPath` is
    // still the pre-filter value at this point — rewriting from it rewound the
    // address and threw the filter straight back out.
    setApplyFilter(!applyFilter);
  };

  const handleSearch = (search) => {
    setQuery(search.length !== 0 ? [search] : []);
    // setFilterProps([]);
    // setFacetFilters([]);
    setPage(0);
    setApplyFilter(!applyFilter);
    // Put the term in the address bar so a reload or a shared link restores the
    // same results and pagination composes with it. `buildSearchHref` drops the
    // page: a new search starts at the beginning.
    // Only when the term actually changes what is in the URL.
    //
    // This is handed to the filter component as `onSearchChange`, so it fires
    // while a filter is being applied — with an empty term and a `router.asPath`
    // that has not caught up with the filter's own push yet. Writing then
    // rewound the address to its pre-filter state and wiped the filter out.
    if (getSearchFromQuery(router.query) !== search) {
      router.replace(buildSearchHref(router.asPath, search), undefined, {
        shallow: true,
        scroll: false,
      });
    }
  };

  const handleClick = (url, internal = false) => {
    if (!url) {
      console.warn('URL is undefined or invalid');
      return;
    }

    if (internal) {
      router.push(url);
    } else {
      const finalUrl =
        url.startsWith('http://') || url.startsWith('https://')
          ? url
          : `https://${url}`;

      window.open(finalUrl, '_blank');
    }
  };

  return (
    <div id="partners-cards-container" className="partners-cards-main">
      <ResourceFilter
        metrosLabel={isItUndefined(global.field_metro_label) || 'Metros'}
        singleFilter={true}
        clearQuery={clearQuery}
        isSearchActive={isSearchActive}
        isFilterActive={isFilterActive}
        setIsSearchActive={setIsSearchActive}
        setIsFilterActive={setIsFilterActive}
        search={isItUndefined(global.field_search_text_translation)}
        // title={isItUndefined(data.field_filter_resources)}
        filterProps={filterProps}
        triggerFilter={triggerFilter}
        data={assemble(categories)}
        onSearchChange={handleSearch}
        handleFilterApply={setApplyFilter}
        searchQuery={query}
        setQuery={setQuery}
        languageFilter={languageFilter}
        setLanguageFilter={setLanguageFilter}
        screenWidth={windowSize.width}
        clearAll={clearAll}
        title={isItUndefined(global.field_filter_text)}
        setFilter={setFilter}
        filter={filter}
        cancelText={isItUndefined(global.field_cancel_text_translation)}
        applyFilterText={isItUndefined(global.field_apply_filters_text)}
        clearAllText={isItUndefined(global.field_clear_all_text)}
        selectedText={isItUndefined(global.field_selected_text)}
        seeMoreText={isItUndefined(global?.field_see_more_text)}
        seeLessText={isItUndefined(global?.field_see_less)}
        isPartnerPage={true}
        placement={SearchBarPlacement.PARTNERS}
        dataLayerFilterName={FilterName.PARTNERS}
      />
      {!isLoading ? (
        algoliaData.nbHits != 0 ? (
          <>
            <div className="partners-cards-container">
              {/*{algoliaData.hits*/}
              {/*    ?.sort((a, b) => {*/}
              {/*    // Ensure featured partners stay on top*/}
              {/*    if (a?.field_featured_partner && !b?.field_featured_partner)*/}
              {/*      return -1;*/}
              {/*    if (!a?.field_featured_partner && b?.field_featured_partner)*/}
              {/*    return 1;*/}

              {/*    // Sort alphabetically if both are featured or both are not*/}
              {/*    return a?.field_partner_name?.localeCompare(*/}
              {/*        b?.field_partner_name*/}
              {/*      );*/}
              {/*    })*/}
              {/*    .map((partner, index) => {*/}
              {algoliaData.hits
                ?.map((partner, index) => {
                  return (
                    <div
                      key={`partner-card-${partner?.field_partner_name || 'partner'}`}
                      className={`partner-card ${partner?.field_featured_partner ? 'featured-partner' : ''}`}
                      onClick={() => {
                        pushSearchSuggestionClickEvent(
                          Array.isArray(query)
                            ? (query[0]?.toLowerCase() ?? '')
                            : (query?.toLowerCase() ?? ''),
                          SearchBarPlacement.PARTNERS,
                          partner?.field_partner_name,
                          index + 1
                        );
                        sendClickedObjectIDsAfterSearch(algoliaData.queryID, partner.objectID, algoliaDataset, index + 1);
                        partner?.field_partner_internal_url
                          ? handleClick(
                              partner?.field_partner_internal_url,
                              true
                            )
                          : handleClick(partner?.field_partner_url, false);
                      }}
                    >
                      {partner?.field_featured_partner && (
                        <span className="badge">Featured partner</span>
                      )}
                      {partner?.field_partner_url &&
                      !partner?.field_partner_internal_url ? (
                        <svg
                          className="external-link-icon"
                          width="32"
                          height="32"
                          viewBox="0 0 32 32"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g clipPath="url(#clip0_31833_35843)">
                            <path
                              d="M21 0C20.45 0 20 0.45 20 1C20 1.55 20.45 2 21 2H28.5875L13.2937 17.2938C12.9062 17.6813 12.9062 18.3187 13.2937 18.7062C13.6812 19.0938 14.3188 19.0938 14.7063 18.7062L30 3.4125V11C30 11.55 30.45 12 31 12C31.55 12 32 11.55 32 11V1C32 0.45 31.55 0 31 0H21ZM4 2C1.79375 2 0 3.79375 0 6V28C0 30.2062 1.79375 32 4 32H26C28.2062 32 30 30.2062 30 28V19C30 18.45 29.55 18 29 18C28.45 18 28 18.45 28 19V28C28 29.1063 27.1063 30 26 30H4C2.89375 30 2 29.1063 2 28V6C2 4.89375 2.89375 4 4 4H13C13.55 4 14 3.55 14 3C14 2.45 13.55 2 13 2H4Z"
                              fill="black"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_31833_35843">
                              <rect width="32" height="32" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                      ) : null}
                      <img
                        src={partner?.url}
                        alt={partner?.field_partner_name}
                        className="partner-logo"
                      />

                      <p className="partner-name">
                        {partner?.field_partner_name}
                      </p>
                      <p className="partner-description">
                        {partner?.field_partner_short_description}
                      </p>

                      {partner?.field_partner_internal_url ? (
                        <Link
                          href={correctInternalLink(
                            partner?.field_partner_internal_url
                          )}
                          passHref
                          legacyBehavior
                        >
                          <a
                            target={
                              isLinkAbsolute(
                                partner?.field_partner_internal_url
                              )
                                ? '_blank'
                                : '_self'
                            }
                            // className="link-internal"
                            className="cta>-link"
                            onClick={(event) => event.stopPropagation()} // Prevent parent click event
                          >
                            {linkText}
                            <img
                              alt="arrow"
                              className="more-arrow"
                              src="/images/learn-more-arrow.svg"
                            />
                          </a>
                        </Link>
                      ) : null}
                    </div>
                  );
                })}
            </div>
            <div className="load-more-container">
              {hasMoreAlgoliaPages(algoliaData) && algoliaData.nbHits > 8 ? (
                <PaginationLink
                  page={page + 1}
                  onLoadMore={loadMore}
                  className="load-more-btn"
                >
                  {isItUndefined(global?.field_load_more_text)}
                </PaginationLink>
              ) : algoliaData.nbHits > 8 ? (
                <button onClick={handleShowLess} className="see-less-btn">
                  {isItUndefined(global?.field_show_less_text)}
                </button>
              ) : null}
            </div>
          </>
        ) : (
          <div className={'no-results-wrapper'}>
            <div className={'inner-container'}>
              <div className={'no-results'}>
                {isItUndefined(global.field_no_results_text)
                  ? isItUndefined(global.field_no_results_text)
                  : 'No results were found. Please check the spelling, reset any filters and try again.'}
              </div>
            </div>
          </div>
        )
      ) : (
        <Loading />
      )}
    </div>
  );
};

export default PartnersCardsBlock;
